import React, { useState, useEffect } from 'react';
import { paymentRemindersService } from '../../services';
import { itinerariesService } from '../../services/itinerariesService';

// Voyager Pro reskin (Figma PaymentRemindersPage) - list+drawer pattern
// matching Leads/Groups/Quotes/Itineraries/Payment Links. Already fully
// unified (independentPaymentRemindersService.js hits the same /payment-reminders
// endpoints with just a display-field adapter), so no DB migration needed.
//
// Real reminders only have 3 stored statuses (scheduled/sent/failed) -
// Figma's 4th "overdue" is a computed display state (due_date has passed
// while still scheduled), not a real stored status; "cancelled" from Figma
// has no equivalent - reminders can only be deleted, not cancelled.

const STATUS_CFG = {
  scheduled: { label: 'Scheduled', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500' },
  sent: { label: 'Sent', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  failed: { label: 'Failed', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
  overdue: { label: 'Overdue', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
};

const TYPE_LABELS = {
  deposit: 'Deposit',
  final_payment: 'Final Payment',
  installment: 'Installment',
  balance_due: 'Balance Due',
};

const inputCls = "w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors";
const labelCls = "block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-1.5";

const fmtMoney = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
const isOverdue = (reminder) => reminder.status === 'scheduled' && new Date(reminder.due_date) < new Date();
const displayStatus = (reminder) => isOverdue(reminder) ? 'overdue' : reminder.status;

// Backend GET /payment-reminders never actually returns a plain `client_name`
// field (pre-existing bug predating this reskin, confirmed via QA testing) -
// only `client_first_name`/`client_last_name` derived from the client's
// lead_traveler JSON. Compute it client-side instead of showing blank.
const getClientName = (reminder) => {
  const name = `${reminder.client_first_name || ''} ${reminder.client_last_name || ''}`.trim();
  return name || reminder.client_name || 'Unknown Client';
};

const BLANK_FORM = {
  itinerary_id: '', payment_type: 'deposit', payment_amount: '',
  reminder_date: '', reminder_time: '', due_date: '', custom_message: '',
  notify_agent: true, notify_agent_sms: false, notify_client: true, notify_client_sms: false,
};

// ─── Create / Edit modal ────────────────────────────────────────────────────

function ReminderFormModal({ initial, itineraries, onSave, onClose }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => {
    if (!initial) return BLANK_FORM;
    const d = new Date(initial.reminder_date);
    return {
      itinerary_id: initial.itinerary_id,
      payment_type: initial.payment_type,
      payment_amount: initial.payment_amount,
      reminder_date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
      reminder_time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
      due_date: initial.due_date.split('T')[0],
      custom_message: initial.custom_message || '',
      notify_agent: initial.notify_agent, notify_agent_sms: initial.notify_agent_sms,
      notify_client: initial.notify_client, notify_client_sms: initial.notify_client_sms,
    };
  });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const valid = (isEdit || form.itinerary_id) && form.payment_amount && form.reminder_date && form.reminder_time && form.due_date;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: '92vh' }}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)] flex-shrink-0">
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">{isEdit ? 'Edit Reminder' : 'Create Payment Reminder'}</h2>
            <p className="text-xs text-[var(--muted-foreground)] mt-0.5">{isEdit ? 'Update reminder details.' : 'Schedule an automated reminder for a client payment.'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          {!isEdit && (
            <div>
              <label className={labelCls}>Itinerary *</label>
              <select className={inputCls} value={form.itinerary_id} onChange={e => set('itinerary_id', e.target.value)}>
                <option value="">Select an itinerary</option>
                {itineraries.map(it => <option key={it.id} value={it.id}>{it.title} - {it.first_name} {it.last_name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Payment Type *</label>
              <select className={inputCls} value={form.payment_type} onChange={e => set('payment_type', e.target.value)}>
                {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Payment Amount *</label>
              <input type="number" step="0.01" className={inputCls} value={form.payment_amount} onChange={e => set('payment_amount', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Reminder Date *</label>
              <input type="date" className={inputCls} value={form.reminder_date} onChange={e => set('reminder_date', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Reminder Time *</label>
              <input type="time" className={inputCls} value={form.reminder_time} onChange={e => set('reminder_time', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Payment Due Date *</label>
            <input type="date" className={inputCls} value={form.due_date} onChange={e => set('due_date', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Custom Message (Optional)</label>
            <textarea rows={3} className={inputCls} value={form.custom_message} onChange={e => set('custom_message', e.target.value)} placeholder="Add a personalized message to the reminder…" />
          </div>
          <div>
            <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Notification Settings</h3>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 text-sm text-[var(--foreground)]"><input type="checkbox" checked={form.notify_agent} onChange={e => set('notify_agent', e.target.checked)} />Notify Agent by Email</label>
              <label className="flex items-center gap-2 text-sm text-[var(--foreground)]"><input type="checkbox" checked={form.notify_agent_sms} onChange={e => set('notify_agent_sms', e.target.checked)} />Notify Agent by SMS</label>
              <label className="flex items-center gap-2 text-sm text-[var(--foreground)]"><input type="checkbox" checked={form.notify_client} onChange={e => set('notify_client', e.target.checked)} />Notify Client by Email</label>
              <label className="flex items-center gap-2 text-sm text-[var(--foreground)]"><input type="checkbox" checked={form.notify_client_sms} onChange={e => set('notify_client_sms', e.target.checked)} />Notify Client by SMS</label>
            </div>
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-[var(--border)] flex-shrink-0">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">Cancel</button>
          <button
            onClick={() => valid && onSave(form)}
            disabled={!valid}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-105 transition-all"
            style={{ backgroundColor: 'var(--primary)' }}
          >
            {isEdit ? 'Update Reminder' : 'Create Reminder'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail drawer ──────────────────────────────────────────────────────────

function ReminderDetailDrawer({ reminder, onClose, onSendNow, onEdit, onDelete }) {
  const status = displayStatus(reminder);
  const cfg = STATUS_CFG[status];
  return (
    <div className="flex flex-col h-full bg-white border-l border-[var(--border)] overflow-hidden w-full sm:w-[400px] flex-shrink-0">
      <div className="flex-shrink-0 p-5 border-b border-[var(--border)]">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)] leading-tight truncate">{reminder.itinerary_title}</div>
            <div className="text-xs text-[var(--muted-foreground)] mt-0.5 truncate">{getClientName(reminder)}</div>
          </div>
          <button onClick={onClose} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] flex-shrink-0 p-1.5 rounded-lg hover:bg-[var(--muted)]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
          {cfg.label}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div>
          <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Reminder Details</h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Payment Type</span><span className="font-medium text-[var(--foreground)]">{TYPE_LABELS[reminder.payment_type] || reminder.payment_type}</span></div>
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Amount</span><span className="font-bold text-[var(--foreground)]">{fmtMoney(reminder.payment_amount)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Reminder</span><span className="font-medium text-[var(--foreground)]">{fmtDate(reminder.reminder_date)} · {fmtTime(reminder.reminder_date)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Due Date</span><span className={`font-medium ${status === 'overdue' ? 'text-red-600' : 'text-[var(--foreground)]'}`}>{fmtDate(reminder.due_date)}</span></div>
            {reminder.status === 'sent' && reminder.sent_at && (
              <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Sent</span><span className="font-medium text-[var(--foreground)]">{fmtDate(reminder.sent_at)}</span></div>
            )}
          </div>
        </div>
        {reminder.custom_message && (
          <div>
            <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Custom Message</h3>
            <div className="p-3 bg-[var(--muted)]/40 rounded-xl text-sm text-[var(--foreground)] whitespace-pre-wrap">{reminder.custom_message}</div>
          </div>
        )}
        <div>
          <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Notifications</h3>
          <div className="flex flex-wrap gap-1.5">
            {reminder.notify_agent && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--muted)] text-[var(--foreground)]">Agent Email</span>}
            {reminder.notify_agent_sms && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--muted)] text-[var(--foreground)]">Agent SMS</span>}
            {reminder.notify_client && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--muted)] text-[var(--foreground)]">Client Email</span>}
            {reminder.notify_client_sms && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--muted)] text-[var(--foreground)]">Client SMS</span>}
          </div>
        </div>
      </div>

      {reminder.status === 'scheduled' && (
        <div className="flex-shrink-0 p-4 border-t border-[var(--border)] space-y-2">
          <button onClick={() => onSendNow(reminder.id)} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all" style={{ backgroundColor: 'var(--primary)' }}>Send Now</button>
          <button onClick={() => onEdit(reminder)} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">Edit</button>
          <button onClick={() => onDelete(reminder.id)} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition-colors">Delete</button>
        </div>
      )}
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

const PaymentRemindersList = () => {
  const [reminders, setReminders] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [selectedReminder, setSelectedReminder] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [remindersResponse, itinerariesResponse] = await Promise.all([
        paymentRemindersService.getAll(),
        itinerariesService.getAll(),
      ]);
      setReminders(Array.isArray(remindersResponse.data || remindersResponse) ? (remindersResponse.data || remindersResponse) : []);
      setItineraries(Array.isArray(itinerariesResponse.data || itinerariesResponse) ? (itinerariesResponse.data || itinerariesResponse) : []);
      setError('');
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load payment reminders');
    } finally {
      setLoading(false);
    }
  };

  const filteredReminders = reminders.filter(r => statusFilter === 'all' || displayStatus(r) === statusFilter);

  const stats = {
    scheduled: reminders.filter(r => r.status === 'scheduled' && !isOverdue(r)).length,
    sent: reminders.filter(r => r.status === 'sent').length,
    overdue: reminders.filter(r => isOverdue(r)).length,
  };

  const openCreateModal = () => { setEditingReminder(null); setShowFormModal(true); };
  const openEditModal = (reminder) => { setEditingReminder(reminder); setShowFormModal(true); };

  const handleSaveForm = async (form) => {
    try {
      const localDateTime = new Date(`${form.reminder_date}T${form.reminder_time}:00`);
      const submissionData = { ...form, reminder_date: localDateTime.toISOString() };
      if (editingReminder) {
        await paymentRemindersService.update(editingReminder.id, submissionData);
      } else {
        await paymentRemindersService.create(submissionData);
      }
      setShowFormModal(false);
      setEditingReminder(null);
      setSelectedReminder(null);
      await fetchData();
    } catch (err) {
      console.error('Error saving reminder:', err);
      alert(`Failed to ${editingReminder ? 'update' : 'create'} payment reminder`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment reminder?')) return;
    try {
      await paymentRemindersService.delete(id);
      setSelectedReminder(null);
      await fetchData();
    } catch (err) {
      console.error('Error deleting reminder:', err);
      alert('Failed to delete payment reminder');
    }
  };

  const handleSendNow = async (id) => {
    if (!window.confirm('Are you sure you want to send this reminder now?')) return;
    try {
      const result = await paymentRemindersService.sendNow(id);
      setSelectedReminder(null);
      await fetchData();
      // the server's message says what actually went out (and what did not)
      alert(result?.message || 'Payment reminder sent successfully!');
    } catch (err) {
      console.error('Error sending reminder:', err);
      await fetchData();
      alert(err.response?.data?.message || 'Failed to send payment reminder');
    }
  };

  if (loading) {
    return <div className="voyager-app flex items-center justify-center min-h-[400px] text-sm text-[var(--muted-foreground)]">Loading payment reminders…</div>;
  }

  return (
    <div className="voyager-app flex h-full min-h-0">
      <div className={`flex-1 overflow-y-auto min-w-0 ${selectedReminder ? 'hidden lg:block' : ''}`}>
        <div className="max-w-5xl mx-auto p-6 space-y-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">Payment Reminders</h1>
              <p className="text-[var(--muted-foreground)] text-sm">Schedule automated reminders for upcoming client payments.</p>
            </div>
            <button onClick={openCreateModal} className="flex items-center gap-2 px-4 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all shadow-sm">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Create Reminder
            </button>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white border border-[var(--border)] rounded-xl p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.scheduled}</div>
              <div className="text-xs text-[var(--muted-foreground)] mt-0.5">Scheduled</div>
            </div>
            <div className="bg-white border border-[var(--border)] rounded-xl p-4">
              <div className="text-2xl font-bold text-emerald-600">{stats.sent}</div>
              <div className="text-xs text-[var(--muted-foreground)] mt-0.5">Sent</div>
            </div>
            <div className="bg-white border border-[var(--border)] rounded-xl p-4">
              <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
              <div className="text-xs text-[var(--muted-foreground)] mt-0.5">Overdue</div>
            </div>
          </div>

          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none cursor-pointer">
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_CFG).map(([v, cfg]) => <option key={v} value={v}>{cfg.label}</option>)}
          </select>

          {filteredReminders.length === 0 ? (
            <div className="text-center py-16 bg-white border-2 border-dashed border-[var(--border)] rounded-2xl">
              <div className="text-4xl mb-3">🔔</div>
              <div className="text-sm font-medium text-[var(--foreground)] mb-1">No payment reminders scheduled</div>
              <div className="text-xs text-[var(--muted-foreground)] mb-4">Schedule a reminder to stay on top of upcoming client payments.</div>
              <button onClick={openCreateModal} className="px-5 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all">Create Your First Reminder</button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredReminders.map(reminder => {
                const status = displayStatus(reminder);
                const cfg = STATUS_CFG[status];
                return (
                  <button
                    key={reminder.id}
                    onClick={() => setSelectedReminder(reminder)}
                    className="w-full text-left bg-white border border-[var(--border)] rounded-xl p-4 hover:border-[var(--accent)] transition-colors flex items-center gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm truncate text-[var(--foreground)]">{reminder.itinerary_title}</span>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                          {cfg.label}
                        </span>
                      </div>
                      <div className="text-xs text-[var(--muted-foreground)] mt-1 truncate">{getClientName(reminder)} · {TYPE_LABELS[reminder.payment_type] || reminder.payment_type} · Due {fmtDate(reminder.due_date)}</div>
                    </div>
                    <div className="text-sm font-bold text-[var(--foreground)] flex-shrink-0">{fmtMoney(reminder.payment_amount)}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {selectedReminder && (
        <ReminderDetailDrawer
          reminder={selectedReminder}
          onClose={() => setSelectedReminder(null)}
          onSendNow={handleSendNow}
          onEdit={openEditModal}
          onDelete={handleDelete}
        />
      )}

      {showFormModal && (
        <ReminderFormModal
          initial={editingReminder}
          itineraries={itineraries}
          onSave={handleSaveForm}
          onClose={() => { setShowFormModal(false); setEditingReminder(null); }}
        />
      )}
    </div>
  );
};

export default PaymentRemindersList;
